import * as _ from "underscore";
import * as localisationHelper from "localisationHelper";

// Models
import { ZebraToMomentFormatOptions } from "Content/ts/Models/Shared/DateTime/dateTimeFormatter";

export const Deps = {
    LocalisationHelper: localisationHelper
};

// Purpose  :   Zebra uses PHP style dates and times whilst Moment prefers an ISO8601 format. Due to this the primary purpose of 
//              this service is to bridge the gap between these technologies without having to create yet more database configuration.
// Sources  :   https://momentjs.com/docs/
//              https://www.php.net/manual/en/function.date.php
// Solution :   https://stackoverflow.com/questions/30186611/php-dateformat-to-moment-js-format
export module DateTimeFormatterService {
    export const UrlBasedDateFormat = "YYYY-MM-DD" as string; // Purpose    : Expected format for URL based dates to function with server based calls
    export const InternalMomentTimeFormat = "HH:mm" as string;
    export const InternalMomentDateFormat = "DD MMM YYYY" as string;
    export const TimeZoneAgnosticAjaxFormat = "YYYY-MM-DDTHH:mm" as string;
    export const LocalisedMomentLongDateFormat = Deps.LocalisationHelper.getLocaleSettings().JsLongDateFormat as string;
    export const LocalisedMomentShortDateFormat = Deps.LocalisationHelper.getLocaleSettings().JsShortDateFormat as string;
    export const LocalisedZebraTimeFormat = Deps.LocalisationHelper.getLocaleSettings().ShowMeridian ? "h:i A" : "H:i" as string;
    export const LocalisedMomentTimeFormat = Deps.LocalisationHelper.getLocaleSettings().ShowMeridian ? "hh:mm A" : "HH:mm" as string;

    export function RetrieveZebraDateFormat(): string {
        return _.reduce(ZebraToMomentFormats(), (previous: string, option: ZebraToMomentFormatOptions) => {
            return previous.replace(option.MomentFormat, option.ZebraFormat);
        }, LocalisedMomentShortDateFormat);
    };

    export function RetrieveZebraTimeFormat(): string {
        return _.reduce(ZebraToMomentFormats(), (previous: string, option: ZebraToMomentFormatOptions) => {
            return previous.replace(option.MomentFormat, option.ZebraFormat);
        }, LocalisedMomentTimeFormat);
    };

    // Source   :   https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
    export function AppendOrdinalSuffix(day: number): string {
        const j = day % 10,
            k = day % 100;

        if (j == 1 && k != 11) {
            return `${day}st`;
        }

        if (j == 2 && k != 12) {
            return `${day}nd`;
        }

        if (j == 3 && k != 13) {
            return `${day}rd`;
        }

        return `${day}th`;
    };

    function ZebraToMomentFormats(): Array<ZebraToMomentFormatOptions> {
        return [
            // Special Character Formats
            { ZebraFormat: "/", MomentFormat: "/" },
            { ZebraFormat: "-", MomentFormat: "-" },
            { ZebraFormat: ":", MomentFormat: ":" },
            { ZebraFormat: " ", MomentFormat: " " },

            // Standard Mapped Formats
            { ZebraFormat: "A", MomentFormat: "A" },                            // Uppercase Ante meridiem and Post meridiem
            { ZebraFormat: "a", MomentFormat: "a" },                            // Lowercase Ante meridiem and Post meridiem
            { ZebraFormat: "D", MomentFormat: "ddd" },
            { ZebraFormat: "d", MomentFormat: "DD" },
            { ZebraFormat: "F", MomentFormat: "MMMM" },
            { ZebraFormat: "H", MomentFormat: "HH" },
            { ZebraFormat: "h", MomentFormat: "hh" },
            { ZebraFormat: "i", MomentFormat: "mm" },
            { ZebraFormat: "j", MomentFormat: "D" },
            { ZebraFormat: "l", MomentFormat: "dddd" },
            { ZebraFormat: "M", MomentFormat: "MMM" },
            { ZebraFormat: "m", MomentFormat: "MM" },            
            { ZebraFormat: "s", MomentFormat: "ss" },
            { ZebraFormat: "Y", MomentFormat: "YYYY" },
            { ZebraFormat: "y", MomentFormat: "YY" }
        ] as Array<ZebraToMomentFormatOptions>;
    }
}
